// src/components/Register.tsx

import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Col, Grid, Row, Select, Tour, Typography, notification, Input, Divider, Progress } from "antd";
import type { TourProps } from "antd";
import { useNavigate } from "react-router-dom";
import { EyeOutlined, EyeInvisibleOutlined, SmileOutlined, QuestionCircleOutlined, EditOutlined } from "@ant-design/icons";

// ─── Custom Components ───────────────────────────────────────────────────────
import CustomButton from "../Shared/customButton";
import HintInput from "../Shared/hintInput";
import InputWithIcons from "../Shared/InputWithIcons";
import CustomLabel from "../Shared/customLabel";

// ─── Assets ──────────────────────────────────────────────────────────────────
import logo from "../../../assets/logo.svg";
import star from "../../../assets/vectors/Star.svg";

// ─── Store & Hooks ───────────────────────────────────────────────────────────
import { useLanguage } from "stores/useLanguage";
import { useAvailableLanguages } from "hooks/useAvailableLanguages";
import { useTranslation } from "hooks/useTranslation";
import { useTour } from "hooks/useTour";

// ─── Service ─────────────────────────────────────────────────────────────────
import { useRequestCode, useConfirmCode, useResendCode } from "api/services/accountService";
import { ColorPalette } from "../Shared/color/colorPalette";

// ─── Constants ───────────────────────────────────────────────────────────────
const K = {
  PAGE_TITLE: 1047, FIRST_NAME: 1048, LAST_NAME: 1049, PHONE: 1050, INVITE_CODE: 1051,
  PASSWORD: 1052, CONFIRM_PASSWORD: 1053, NEXT_STEP: 1054, OTP_LABEL: 1055, EMAIL: 1056,
  COMPANY_NAME: 1057, REGISTRY_CODE: 1058, SUBMIT: 1059, RESEND_TIMER: 1060, ALREADY_REGISTERED: 1061, LOGIN_LINK: 1062,
    RESEND: 1098,  
  // ─── Tour Keys Step 1 ───
  TOUR_LOGIN_LINK_TITLE: 1063,   
  TOUR_LOGIN_LINK_DESC: 1064,    
  TOUR_FIRST_NAME_TITLE: 1087,
  TOUR_FIRST_NAME_DESC: 1088,
  TOUR_LAST_NAME_TITLE: 1089,
  TOUR_LAST_NAME_DESC: 1090,
  TOUR_PHONE_TITLE: 1067,
  TOUR_PHONE_DESC: 1068,
  TOUR_INVITE_TITLE: 1071,
  TOUR_INVITE_DESC: 1072,
  TOUR_PASS_TITLE: 1073,
  TOUR_PASS_DESC: 1074,
  TOUR_CONFIRM_PASS_TITLE: 1075,
  TOUR_CONFIRM_PASS_DESC: 1076,
  TOUR_NEXT_TITLE: 1069,
  TOUR_NEXT_DESC: 1070,

  // ─── Tour Keys Step 2 ───
  TOUR_OTP_TITLE: 1077,
  TOUR_OTP_DESC: 1078,
  TOUR_EMAIL_TITLE: 1079,
  TOUR_EMAIL_DESC: 1080,
  TOUR_COMPANY_TITLE: 1081,
  TOUR_COMPANY_DESC: 1082,
  TOUR_REGISTRY_TITLE: 1083,
  TOUR_REGISTRY_DESC: 1084,
  TOUR_SUBMIT_TITLE: 1085,
  TOUR_SUBMIT_DESC: 1086,
  TOUR_NEXT: 1017,
  TOUR_PREV: 1018,
  TOUR_FINISH: 1019,
  TOUR_SKIP: 1020,
  // ─── Error Messages ───
  ERR_PHONE_INVALID: 1008, 
  ERR_PASS_REQUIRED: 1009, 
  ERR_REQUIRED: 1091,      
  ERR_INVITE_LENGTH: 1092, 
  ERR_PASS_MISMATCH: 1093, 
  ERR_EMAIL_INVALID: 1094, 

  // ─── New Keys (To be added to DB) ───
  EDIT_PHONE_BTN: 1095,
  TOUR_EDIT_PHONE_TITLE: 1096,
  TOUR_EDIT_PHONE_DESC: 1097,
};

const ALL_KEYS = Object.values(K) as number[];

// ─── Validation ──────────────────────────────────────────────────────────────
const PHONE_REGEX = /^09[0-9]{9}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const { useBreakpoint } = Grid;

// ════════════════════════════════════════════════════════════════════════════
// ─── Types ──────────────────────────────────────────────────────────────────
// ════════════════════════════════════════════════════════════════════════════

interface LanguageSelectorProps {
  currentCode: string;
  languages: { code: string; nativeName: string }[];
  isRtl: boolean;
  setCurrentCode: (code: string) => void;
  style?: React.CSSProperties;
}

interface FormContentProps {
  isMobile: boolean;
  direction: string;
  step: 1 | 2;
  form: any;
  errors: Record<string, string>;
  showPassword: boolean;
  showConfirmPass: boolean;
  inviteStatus: { isEmailInvitation: boolean; invitationType: number };
  timer: number;
  reqCodePending: boolean;
  confirmCodePending: boolean;
  resendCodePending: boolean;
  // Refs
  loginLinkRef: React.RefObject<HTMLDivElement | null>;
  firstNameRef: React.RefObject<HTMLDivElement | null>;
  lastNameRef: React.RefObject<HTMLDivElement | null>;
  phoneRef: React.RefObject<HTMLDivElement | null>;
  inviteCodeRef: React.RefObject<HTMLDivElement | null>;
  passwordRef: React.RefObject<HTMLDivElement | null>;
  confirmPasswordRef: React.RefObject<HTMLDivElement | null>;
  nextBtnRef: React.RefObject<HTMLDivElement | null>;
  otpRef: React.RefObject<HTMLDivElement | null>;
  emailRef: React.RefObject<HTMLDivElement | null>;
  companyRef: React.RefObject<HTMLDivElement | null>;
  registryRef: React.RefObject<HTMLDivElement | null>;
  submitBtnRef: React.RefObject<HTMLDivElement | null>;
  editPhoneRef: React.RefObject<HTMLDivElement | null>;
  // Handlers
  t: (key: number) => string;
  handleUpdate: (field: string, value: string) => void;
  setShowPassword: (v: boolean) => void;
  setShowConfirmPass: (v: boolean) => void;
  onNextStep: () => void;
  onSubmit: () => void;
  onResend: () => void;
  onBackToStep1: () => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  navigate: ReturnType<typeof useNavigate>;
}

interface LayoutProps {
  direction: string;
  currentCode: string;
  languages: { code: string; nativeName: string }[];
  isRtl: boolean;
  setCurrentCode: (code: string) => void;
  t: (key: number) => string;
  navigate: ReturnType<typeof useNavigate>;
  formContentProps: FormContentProps;
  onStartTour: () => void;
}

// ════════════════════════════════════════════════════════════════════════════
// ─── Sub Components ──────────────────────────────────────────────────────────
// ════════════════════════════════════════════════════════════════════════════

const LanguageSelector = ({
  currentCode,
  languages,
  isRtl,
  setCurrentCode,
  style,
}: LanguageSelectorProps) => (
  <Select
    value={currentCode}
    onChange={(code) => setCurrentCode(code)}
    size="small"
    style={{ width: 120, ...style }}
    options={languages.map((lang) => ({
      value: lang.code,
      label: lang.nativeName,
    }))}
  />
);

const TopLoginDivider = ({ 
  t, 
  navigate,
  loginLinkRef 
}: { 
  t: any; 
  navigate: any; 
  loginLinkRef: React.RefObject<HTMLDivElement | null> 
}) => (
  <div style={{ marginBottom: 32 }}>
    <Divider style={{ borderColor: ColorPalette.gray[300], marginTop: 0, marginBottom: 12 }}>
      <span className="iran" style={{ color: ColorPalette.gray[600], fontSize: 14, lineHeight: 1 }}>
        {t(K.ALREADY_REGISTERED)}
      </span>
    </Divider>
    <div ref={loginLinkRef} style={{ textAlign: "center" }}>
      <CustomButton
        onClick={() => navigate("/login")}
        
        hasTitle
        customType="Secondary"
        size="large"
        style={{ padding: 0, fontSize: 15, fontWeight: "bold", width: "100%", color: ColorPalette.primaryBlue[600], textDecoration: "none" }}
      >
        {t(K.LOGIN_LINK)}
      </CustomButton>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────

const FormContent = ({
  isMobile, direction, step, form, errors, showPassword, showConfirmPass,
  inviteStatus, timer, reqCodePending, confirmCodePending, resendCodePending,
  loginLinkRef, firstNameRef, lastNameRef, phoneRef, inviteCodeRef, passwordRef, confirmPasswordRef, nextBtnRef,
  otpRef, emailRef, companyRef, registryRef, submitBtnRef, editPhoneRef,
  t, handleUpdate, setShowPassword, setShowConfirmPass, onNextStep, onSubmit, onResend, onBackToStep1, handleKeyDown, navigate,
}: FormContentProps) => (
  <div dir={direction} style={{ width: "100%", fontFamily: "YekanBakhVF" }}>
    
    {step === 1 && <TopLoginDivider t={t} navigate={navigate} loginLinkRef={loginLinkRef} />}

    <Row style={{ textAlign: "center", marginBottom: 24 }}>
      <Typography.Text
        className="iran"
        style={{ fontSize: isMobile ? 28 : 40, fontWeight: 700, width: "100%", display: "block", color: ColorPalette.gray[900] }}
      >
        {t(K.PAGE_TITLE)}
      </Typography.Text>
    </Row>

    {step === 1 ? (
      <>
        <Row gutter={16}>
          <Col span={12}>
            <div ref={firstNameRef}>
              <CustomLabel>{t(K.FIRST_NAME)}</CustomLabel>
              <InputWithIcons value={form.firstName} onKeyDown={handleKeyDown} onChange={(e) => handleUpdate("firstName", e.target.value)} size="large" />
              {errors.firstName && <HintInput status="Error">{errors.firstName}</HintInput>}
            </div>
          </Col>
          <Col span={12}>
            <div ref={lastNameRef}>
              <CustomLabel>{t(K.LAST_NAME)}</CustomLabel>
              <InputWithIcons value={form.lastName} onKeyDown={handleKeyDown} onChange={(e) => handleUpdate("lastName", e.target.value)} size="large" />
              {errors.lastName && <HintInput status="Error">{errors.lastName}</HintInput>}
            </div>
          </Col>
        </Row>
        
        <div ref={phoneRef} style={{ marginTop: 16 }}>
          <CustomLabel>{t(K.PHONE)}</CustomLabel>
          <div dir="ltr">
            <InputWithIcons 
              value={form.phoneNumber} 
              onKeyDown={handleKeyDown}
              onChange={(e) => {
                let val = e.target.value.replace(/[^0-9]/g, ""); 
                if (val.length > 11) val = val.slice(0, 11); 
                if (val.length > 0 && val[0] !== "0") val = "";
                if (val.length > 1 && val[1] !== "9") val = "0";
                handleUpdate("phoneNumber", val);
              }} 
              size="large" 
            />
          </div>
          {errors.phoneNumber && <HintInput status="Error">{errors.phoneNumber}</HintInput>}
        </div>

        <div ref={inviteCodeRef} style={{ marginTop: 16 }}>
          <CustomLabel>{t(K.INVITE_CODE)}</CustomLabel>
          <InputWithIcons value={form.invitationCode} onKeyDown={handleKeyDown} onChange={(e) => handleUpdate("invitationCode", e.target.value)} size="large" />
          {errors.invitationCode && <HintInput status="Error">{errors.invitationCode}</HintInput>}
        </div>

        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col span={12}>
            <div ref={passwordRef}>
              <CustomLabel>{t(K.PASSWORD)}</CustomLabel>
              <InputWithIcons type={showPassword ? "text" : "password"} value={form.password} onKeyDown={handleKeyDown} onChange={(e) => handleUpdate("password", e.target.value)} RightIconEnabled RightIcon={showPassword ? EyeOutlined : EyeInvisibleOutlined} onClickRightIcon={() => setShowPassword(!showPassword)} size="large" />
              {errors.password && <HintInput status="Error">{errors.password}</HintInput>}
            </div>
          </Col>
          <Col span={12}>
            <div ref={confirmPasswordRef}>
              <CustomLabel>{t(K.CONFIRM_PASSWORD)}</CustomLabel>
              <InputWithIcons type={showConfirmPass ? "text" : "password"} value={form.confirmPassword} onKeyDown={(e) => { if(e.key === "Enter") onNextStep() }} onChange={(e) => handleUpdate("confirmPassword", e.target.value)} RightIconEnabled RightIcon={showConfirmPass ? EyeOutlined : EyeInvisibleOutlined} onClickRightIcon={() => setShowConfirmPass(!showConfirmPass)} size="large" />
            </div>
          </Col>
        </Row>
        {errors.confirmPassword && <Row style={{ marginTop: 8 }}><HintInput status="Error">{errors.confirmPassword}</HintInput></Row>}

        <div ref={nextBtnRef} style={{ marginTop: 24 }}>
          <CustomButton customType="Primary" loading={reqCodePending} disabled={reqCodePending} onClick={onNextStep} style={{ width: "100%", height: 48, fontSize: 16 }} hasTitle>
            {t(K.NEXT_STEP)}
          </CustomButton>
        </div>
      </>
    ) : (
      <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
        {/* Step 2 Header - Edit Phone */}
        <div ref={editPhoneRef} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", backgroundColor: ColorPalette.gray[50], borderRadius: 8, border: `1px solid ${ColorPalette.gray[200]}` }}>
          <div style={{ display: "flex", flexDirection: "column" }}>
             <Typography.Text type="secondary" style={{ fontSize: 12 }}>{t(K.PHONE)}</Typography.Text>
             <Typography.Text dir="ltr" strong>{form.phoneNumber}</Typography.Text>
          </div>
          <CustomButton customType="Link" onClick={onBackToStep1} hasTitle style={{ padding: 0, height: "auto" }}>
             <EditOutlined style={{ marginInlineEnd: 4 }} />
             {t(K.EDIT_PHONE_BTN)}
          </CustomButton>
        </div>

        {/* Step 2 Content */}
        <div ref={otpRef} style={{ textAlign: "center" }}>
          <div style={{ marginBottom: 12 }}>
            <CustomLabel>{t(K.OTP_LABEL)}</CustomLabel>
          </div>
          <div dir="ltr" style={{ display: "flex", justifyContent: "center" }}>
            <Input.OTP length={6} value={form.otp} onChange={(val) => handleUpdate("otp", val)} />
          </div>
          {errors.otp && <HintInput status="Error">{errors.otp}</HintInput>}
        </div>

        {inviteStatus.isEmailInvitation && (
          <div ref={emailRef}>
            <CustomLabel>{t(K.EMAIL)}</CustomLabel>
            <InputWithIcons value={form.email} onChange={(e) => handleUpdate("email", e.target.value)} size="large" />
            {errors.email && <HintInput status="Error">{errors.email}</HintInput>}
          </div>
        )}

        {inviteStatus.invitationType === 1 && (
          <Row gutter={16}>
            <Col span={12}>
              <div ref={companyRef}>
                <CustomLabel>{t(K.COMPANY_NAME)}</CustomLabel>
                <InputWithIcons value={form.companyName} onChange={(e) => handleUpdate("companyName", e.target.value)} size="large" />
                {errors.companyName && <HintInput status="Error">{errors.companyName}</HintInput>}
              </div>
            </Col>
            <Col span={12}>
              <div ref={registryRef}>
                <CustomLabel>{t(K.REGISTRY_CODE)}</CustomLabel>
                <InputWithIcons value={form.registryCode} onChange={(e) => handleUpdate("registryCode", e.target.value)} size="large" />
                {errors.registryCode && <HintInput status="Error">{errors.registryCode}</HintInput>}
              </div>
            </Col>
          </Row>
        )}

        <div ref={submitBtnRef} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
          {timer > 0 ? (
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Progress 
                type="circle" 
                percent={(timer / 60) * 100} 
                format={() => <span style={{ fontSize: 14 }}>{timer}</span>} 
                size={40} 
                strokeColor={ColorPalette.primaryBlue[600]}
              />
              <Typography.Text type="secondary" style={{ fontSize: 14 }}>{t(K.RESEND_TIMER)}</Typography.Text>
            </div>
          ) : (
            <CustomButton customType="Link" hasTitle onClick={onResend} loading={resendCodePending} style={{ padding: 0 }}>
          {t(K.RESEND)}
            </CustomButton>
          )}
          
          <CustomButton customType="Primary" onClick={onSubmit} loading={confirmCodePending} disabled={confirmCodePending} hasTitle style={{ minWidth: 120 }}>
            {t(K.SUBMIT)}
          </CustomButton>
        </div>
      </div>
    )}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────

const DesktopLayout = ({
  direction, currentCode, languages, isRtl, setCurrentCode, formContentProps, onStartTour
}: LayoutProps) => (
  <Row style={{ height: "100vh" }} dir={direction}>
    <Col style={{ width: "50%", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: ColorPalette.primaryBlue[600], overflow: "hidden", position: "relative" }}>
      <img src={star} style={{ height: "100vh", objectFit: "cover" }} alt="" />
    </Col>
    <Col style={{ width: "50%", display: "flex", flexDirection: "column", position: "relative", padding: "24px", overflowY: "auto" }}>
      
      {/* Top Controls */}
      <div 
        style={{ 
          position: "absolute", 
          top: 24, 
          ...(isRtl ? { left: 24 } : { right: 24 }),
          display: "flex",
          alignItems: "center",
          gap: 12
        }}
      >
        <Button 
          type="text" 
          icon={<QuestionCircleOutlined style={{ fontSize: 20, color: ColorPalette.gray[600] }} />} 
          onClick={onStartTour}
           
        />
        <LanguageSelector currentCode={currentCode} languages={languages} isRtl={isRtl} setCurrentCode={setCurrentCode} />
      </div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "100%", padding: "40px 0" }}>
        <div style={{ width: "100%", maxWidth: 440 }}>
          <Row justify="center" style={{ marginBottom: 32 }}>
            <img src={logo} style={{ width: 160, height: "auto", objectFit: "contain" }} alt="logo" />
          </Row>
          <FormContent {...formContentProps} isMobile={false} />
        </div>
      </div>
    </Col>
  </Row>
);

const MobileLayout = ({
  direction, currentCode, languages, isRtl, setCurrentCode, formContentProps, onStartTour
}: LayoutProps) => (
  <div dir={direction} style={{ minHeight: "100vh", position: "relative", overflow: "hidden", display: "flex", flexDirection: "column" }}>
    <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: ColorPalette.primaryBlue[600], zIndex: 0 }}>
      <img src={star} style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.6 }} alt="" />
    </div>
    <div style={{ position: "relative", zIndex: 1, padding: "24px 16px", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <img src={logo} style={{ width: 100, objectFit: "contain", filter: "brightness(0) invert(1)" }} alt="logo" />
        
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Button 
            type="text" 
            icon={<QuestionCircleOutlined style={{ fontSize: 20, color: "#fff" }} />} 
            onClick={onStartTour}
          />
          <LanguageSelector currentCode={currentCode} languages={languages} isRtl={isRtl} setCurrentCode={setCurrentCode} />
        </div>
      </div>
      <Row justify="center" align="middle" style={{ flex: 1, width: "100%", margin: 0 }}>
        <Card style={{ width: "100%", maxWidth: 400, borderRadius: 16, boxShadow: "0 10px 25px rgba(0,0,0,0.15)", backgroundColor: "#ffffff" }} bodyStyle={{ padding: "32px 24px" }}>
          <FormContent {...formContentProps} isMobile={true} />
        </Card>
      </Row>
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// ─── Main Component ──────────────────────────────────────────────────────────
// ════════════════════════════════════════════════════════════════════════════

const Register = () => {
  const [api, contextHolder] = notification.useNotification(); 
  const screens = useBreakpoint();
  const navigate = useNavigate();

  // ── Language ──
  const { currentCode, direction, setCurrentCode } = useLanguage();
  const { languages } = useAvailableLanguages();
  const isRtl = direction === "rtl";
  const { t } = useTranslation(ALL_KEYS);

  const currentLanguageId = useMemo(() => {
    const idx = languages.findIndex((l) => l.code === currentCode);
    return idx >= 0 ? idx + 1 : 1;
  }, [currentCode, languages]);

  // ── Local States ──
  const [step, setStep] = useState<1 | 2>(1);
  const [form, setForm] = useState({
    firstName: "", lastName: "", phoneNumber: "", invitationCode: "", password: "", confirmPassword: "",
    otp: "", email: "", companyName: "", registryCode: ""
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [inviteStatus, setInviteStatus] = useState({ isEmailInvitation: false, invitationType: 0 });
  const [timer, setTimer] = useState(60);

  // ── Mutations ──
  const reqCodeMut = useRequestCode();
  const confirmCodeMut = useConfirmCode();
  const resendCodeMut = useResendCode();

  // ── Refs Step 1 ──
  const loginLinkRef = useRef<HTMLDivElement>(null);
  const firstNameRef = useRef<HTMLDivElement>(null);
  const lastNameRef = useRef<HTMLDivElement>(null);
  const phoneRef = useRef<HTMLDivElement>(null);
  const inviteCodeRef = useRef<HTMLDivElement>(null);
  const passwordRef = useRef<HTMLDivElement>(null);
  const confirmPasswordRef = useRef<HTMLDivElement>(null);
  const nextBtnRef = useRef<HTMLDivElement>(null);

  // ── Refs Step 2 ──
  const otpRef = useRef<HTMLDivElement>(null);
  const emailRef = useRef<HTMLDivElement>(null);
  const companyRef = useRef<HTMLDivElement>(null);
  const registryRef = useRef<HTMLDivElement>(null);
  const submitBtnRef = useRef<HTMLDivElement>(null);
  const editPhoneRef = useRef<HTMLDivElement>(null);

  // ── Tour ──
  const step1Tour: TourProps["steps"] = [
    { title: t(K.TOUR_LOGIN_LINK_TITLE), description: t(K.TOUR_LOGIN_LINK_DESC), target: () => loginLinkRef.current!, placement: "bottom", nextButtonProps: { children: t(K.TOUR_NEXT) }, prevButtonProps: { children: t(K.TOUR_PREV) } },
    { title: t(K.TOUR_FIRST_NAME_TITLE), description: t(K.TOUR_FIRST_NAME_DESC), target: () => firstNameRef.current!, placement: "bottom", nextButtonProps: { children: t(K.TOUR_NEXT) }, prevButtonProps: { children: t(K.TOUR_PREV) } },
    { title: t(K.TOUR_LAST_NAME_TITLE), description: t(K.TOUR_LAST_NAME_DESC), target: () => lastNameRef.current!, placement: "bottom", nextButtonProps: { children: t(K.TOUR_NEXT) }, prevButtonProps: { children: t(K.TOUR_PREV) } },
    { title: t(K.TOUR_PHONE_TITLE), description: t(K.TOUR_PHONE_DESC), target: () => phoneRef.current!, placement: "bottom", nextButtonProps: { children: t(K.TOUR_NEXT) }, prevButtonProps: { children: t(K.TOUR_PREV) } },
    { title: t(K.TOUR_INVITE_TITLE), description: t(K.TOUR_INVITE_DESC), target: () => inviteCodeRef.current!, placement: "bottom", nextButtonProps: { children: t(K.TOUR_NEXT) }, prevButtonProps: { children: t(K.TOUR_PREV) } },
    { title: t(K.TOUR_PASS_TITLE), description: t(K.TOUR_PASS_DESC), target: () => passwordRef.current!, placement: "bottom", nextButtonProps: { children: t(K.TOUR_NEXT) }, prevButtonProps: { children: t(K.TOUR_PREV) } },
    { title: t(K.TOUR_CONFIRM_PASS_TITLE), description: t(K.TOUR_CONFIRM_PASS_DESC), target: () => confirmPasswordRef.current!, placement: "bottom", nextButtonProps: { children: t(K.TOUR_NEXT) }, prevButtonProps: { children: t(K.TOUR_PREV) } },
    // مرحله آخر step1Tour: استفاده از TOUR_FINISH
    { title: t(K.TOUR_NEXT_TITLE), description: t(K.TOUR_NEXT_DESC), target: () => nextBtnRef.current!, placement: "top", nextButtonProps: { children: t(K.TOUR_FINISH) }, prevButtonProps: { children: t(K.TOUR_PREV) } },
  ];

  const step2Tour: TourProps["steps"] = [
    { title: t(K.TOUR_EDIT_PHONE_TITLE), description: t(K.TOUR_EDIT_PHONE_DESC), target: () => editPhoneRef.current!, placement: "bottom", nextButtonProps: { children: t(K.TOUR_NEXT) }, prevButtonProps: { children: t(K.TOUR_PREV) } },
    { title: t(K.TOUR_OTP_TITLE), description: t(K.TOUR_OTP_DESC), target: () => otpRef.current!, placement: "bottom", nextButtonProps: { children: t(K.TOUR_NEXT) }, prevButtonProps: { children: t(K.TOUR_PREV) } },
    ...(inviteStatus.isEmailInvitation ? [{ title: t(K.TOUR_EMAIL_TITLE), description: t(K.TOUR_EMAIL_DESC), target: () => emailRef.current!, placement: "bottom" as const, nextButtonProps: { children: t(K.TOUR_NEXT) }, prevButtonProps: { children: t(K.TOUR_PREV) } }] : []),
    ...(inviteStatus.invitationType === 1 ? [
      { title: t(K.TOUR_COMPANY_TITLE), description: t(K.TOUR_COMPANY_DESC), target: () => companyRef.current!, placement: "bottom" as const, nextButtonProps: { children: t(K.TOUR_NEXT) }, prevButtonProps: { children: t(K.TOUR_PREV) } },
      { title: t(K.TOUR_REGISTRY_TITLE), description: t(K.TOUR_REGISTRY_DESC), target: () => registryRef.current!, placement: "bottom" as const, nextButtonProps: { children: t(K.TOUR_NEXT) }, prevButtonProps: { children: t(K.TOUR_PREV) } }
    ] : []),
    // مرحله آخر step2Tour: استفاده از TOUR_FINISH
    { title: t(K.TOUR_SUBMIT_TITLE), description: t(K.TOUR_SUBMIT_DESC), target: () => submitBtnRef.current!, placement: "top", nextButtonProps: { children: t(K.TOUR_FINISH) }, prevButtonProps: { children: t(K.TOUR_PREV) } }
  ];

  const activeTourSteps = step === 1 ? step1Tour : step2Tour;
  const { isActive: tourActive, currentStepIndex, nextStep, prevStep, dismissTour, startTour } = useTour("REGISTER_TOUR_" + step, activeTourSteps as any);

  // ── Timer Effect ──
  useEffect(() => {
    if (step === 2 && timer > 0) {
      const interval = setInterval(() => setTimer((p) => p - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [step, timer]);


   // ── Re-validate on Language Change ──
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      if (step === 1) validateStep1();
      else if (step === 2) validateStep2();
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCode]);

  // ── Handlers ──
  const handleUpdate = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateStep1 = () => {
    const newErrs: Record<string, string> = {};
    if (!form.firstName) newErrs.firstName = t(K.ERR_REQUIRED);
    if (!form.lastName) newErrs.lastName = t(K.ERR_REQUIRED);
    if (!PHONE_REGEX.test(form.phoneNumber)) newErrs.phoneNumber = t(K.ERR_PHONE_INVALID);
    if (!form.invitationCode || form.invitationCode.length !== 6) newErrs.invitationCode = t(K.ERR_INVITE_LENGTH);
    if (!form.password) newErrs.password = t(K.ERR_REQUIRED);
    if (form.password !== form.confirmPassword) newErrs.confirmPassword = t(K.ERR_PASS_MISMATCH);
    setErrors(newErrs);
    return Object.keys(newErrs).length === 0;
  };

  const validateStep2 = () => {
    const newErrs: Record<string, string> = {};
    if (form.otp.length !== 6) newErrs.otp = t(K.ERR_REQUIRED);
    if (inviteStatus.isEmailInvitation && !EMAIL_REGEX.test(form.email)) newErrs.email = t(K.ERR_EMAIL_INVALID);
    if (inviteStatus.invitationType === 1) {
      if (!form.companyName) newErrs.companyName = t(K.ERR_REQUIRED);
      if (!form.registryCode) newErrs.registryCode = t(K.ERR_REQUIRED);
    }
    setErrors(newErrs);
    return Object.keys(newErrs).length === 0;
  };

   const onNextStep = () => {
    if (!validateStep1()) return;
    reqCodeMut.mutate({
      phoneNumber: form.phoneNumber,
      invitationCode: Number(form.invitationCode),
      firstName: form.firstName,
      lastName: form.lastName,
      password: form.password,
      confirmPassword: form.confirmPassword,
      languageId: currentLanguageId,
    }, {
      onSuccess: (res) => {
        if (res.statusCode === 400 || res.statusCode !== 200) {
          api.error({ 
            message: res.message || "خطایی رخ داده است",
            placement: isRtl ? "topLeft" : "topRight" 
          });
          return; 
        }
        setInviteStatus({ 
          isEmailInvitation: res.data?.isEmailInvitation, 
          invitationType: res.data?.invitationType 
        });
        setStep(2);
        setTimer(60);
      },
      onError: () => {
        api.error({ message: "خطای ارتباط با سرور", placement: isRtl ? "topLeft" : "topRight" });
      }
    });
  };

  const onSubmit = () => {
    if (!validateStep2()) return;
    confirmCodeMut.mutate({
      phoneNumber: form.phoneNumber,
      verificationCode: form.otp,
      email: inviteStatus.isEmailInvitation ? form.email : undefined,
      companyName: inviteStatus.invitationType === 1 ? form.companyName : undefined,
      registryCode: inviteStatus.invitationType === 1 ? form.registryCode : undefined,
    }, {
      onSuccess: () => {
        api.success({ 
          message: "Registration Successful", 
          icon: <SmileOutlined style={{ color: ColorPalette?.primaryBlue?.[600] || "#1890ff" }} />,
          placement: isRtl ? "topLeft" : "topRight" 
        });
        setTimeout(() => navigate("/login"), 1000);
      }
    });
  };

  const onResend = () => {
    resendCodeMut.mutate({ phoneNumber: form.phoneNumber }, { onSuccess: () => setTimer(60) });
  };

  const onBackToStep1 = () => {
    setStep(1);
    setForm(prev => ({ ...prev, otp: "" }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const focusable = Array.from(document.querySelectorAll<HTMLElement>('input:not([disabled]), [tabindex]:not([tabindex="-1"])'));
      const currentIndex = focusable.indexOf(e.target as HTMLElement);
      if (focusable[currentIndex + 1]) focusable[currentIndex + 1].focus();
    }
  };

  const handleStartTour = () => {
    if (startTour) startTour();
  };

  // ── Props Assembly ──
  const formContentProps: FormContentProps = {
    isMobile: false, direction, step, form, errors, showPassword, showConfirmPass,
    inviteStatus, timer, reqCodePending: reqCodeMut.isPending, confirmCodePending: confirmCodeMut.isPending,
    resendCodePending: resendCodeMut.isPending,
    loginLinkRef, firstNameRef, lastNameRef, phoneRef, inviteCodeRef, passwordRef, confirmPasswordRef, nextBtnRef,
    otpRef, emailRef, companyRef, registryRef, submitBtnRef, editPhoneRef,
    t, handleUpdate, setShowPassword, setShowConfirmPass, onNextStep, onSubmit, onResend, onBackToStep1, handleKeyDown, navigate,
  };

  return (
    <>
      {contextHolder} 
      
      {screens.xs ? (
        <MobileLayout direction={direction} currentCode={currentCode} languages={languages} isRtl={isRtl} setCurrentCode={setCurrentCode} t={t} navigate={navigate} formContentProps={formContentProps} onStartTour={handleStartTour} />
      ) : (
        <DesktopLayout direction={direction} currentCode={currentCode} languages={languages} isRtl={isRtl} setCurrentCode={setCurrentCode} t={t} navigate={navigate} formContentProps={formContentProps} onStartTour={handleStartTour} />
      )}

      {tourActive && (
        <Tour
          open={tourActive}
          onClose={dismissTour}
          steps={activeTourSteps}
          current={currentStepIndex}
          onChange={(s) => {
            if (s > currentStepIndex) nextStep();
            else prevStep();
          }}
        />
      )}
    </>
  );
};

export default Register;
